import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Panel, Table } from 'react-bootstrap';
import { FridgeHealth, LocationIDs } from '../libs/GraphQL';
import Loader from '../components/atoms/Loader';
import Toggle from '../components/atoms/forms/Toggle';
import Select from '../components/atoms/forms/Select';
import styledComponent from '../utils/styledComponent';

function useFormFields(initialState) {
  const [fields, setValues] = useState(initialState);

  return [
    fields,
    function (event) {
      if (event) {
        setValues({
          ...fields,
          [event.target.id]: event.target.value,
        });
      }
    },
  ];
}

const StyledDiv = styledComponent('div', {
  '& .content': {
    display: 'flex',
  },
  '& .count': {
    backgroundColor: '#757575',
    borderRadius: '15px',
    color: '#fff',
    fontSize: '14px',
    marginLeft: '8px',
    padding: '5px 10px',
  },
  '& .countWrapper': {
    alignItems: 'center',
    display: 'flex',
  },
  '.filters': {
    background: '#f8f8f8',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '15px',
    marginRight: '16px',
    width: '500px',
  },
  '& .locationsWrapper': {
    width: '700px',
  },
});

export default function PortfolioHealth(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [healthState, setHealthState] = useState([]);
  const [fields, handleFieldChange] = useFormFields({
    temperature: '',
    hdmi: '',
    elo: '',
    cardReader: '',
    electricalLayout: '',
    jofemarState: '',
    outOfOrder: '',
    safetyHold: '',
    timezone: '',
    inaccessible: '',
  });
  const data = [
    {
      name: 'Inaccessible',
      affected: healthState.filter((loc) => loc.inaccessible === true).length,
      total: healthState.filter((loc) => loc.inaccessible !== true).length,
    },
    {
      name: 'Out of Order',
      affected: healthState.filter((loc) => loc.outOfOrder === true).length,
      total: healthState.filter((loc) => loc.outOfOrder !== true).length,
    },
    {
      name: 'Safety Hold',
      affected: healthState.filter((loc) => loc.safetyHold === true).length,
      total: healthState.filter((loc) => loc.safetyHold !== true).length,
    },
  ];
  const filterFields = [
    // temperature: { type: 'select' },
    { default: true, label: 'HDMI Connected', key: 'hdmi', type: 'bool' },
    { default: true, label: 'ELO Connected', key: 'elo', type: 'bool' },
    {
      default: true,
      label: 'Card Reader Connected',
      key: 'cardReader',
      type: 'bool',
    },
    {
      default: true,
      label: 'Has Electrical Layout',
      key: 'electricalLayout',
      type: 'bool',
    },
    {
      default: false,
      label: 'Jofemar State',
      key: 'jofemarState',
      type: 'select',
      values: fields.jofemarState,
    },
    {
      default: false,
      label: 'Is Out of Order',
      key: 'outOfOrder',
      type: 'bool',
    },
    {
      default: false,
      label: 'Has Safety Hold',
      key: 'safetyHold',
      type: 'bool',
    },
    {
      default: false,
      label: 'Timezone',
      key: 'timezone',
      type: 'select',
      values: fields.timezone,
    },
    {
      default: false,
      label: 'Is Inaccessible',
      key: 'inaccessible',
      type: 'bool',
    },
  ];

  useEffect(() => {
    const fetchHealth = async (locations) => {
      let grabLocations = locations.filter(
        // eslint-disable-next-line no-prototype-builtins
        (location) => !healthState.hasOwnProperty(location.id),
      );
      grabLocations = grabLocations.map((loc) => loc.id);
      const splitLocations = [];
      while (grabLocations.length) {
        splitLocations.push(grabLocations.splice(0, 10));
      }
      const orderPromises = [];
      for (let i = 0; i <= splitLocations.length; i++) {
        if (splitLocations[i]) {
          orderPromises.push(FridgeHealth({ locationIds: splitLocations[i] }));
        }
      }
      Promise.all(orderPromises)
        // eslint-disable-next-line no-shadow
        .then((data) => {
          setIsLoading(false);
          const combinedFridgeHealth = [];
          for (let d = 0; d < data.length; d++) {
            combinedFridgeHealth.push(
              data[d].data.fridgeHealth.reduce(function (acc, cur, i) {
                if (cur && cur.fridgeData.length > 0) {
                  const resultData = cur.fridgeData[0];
                  acc.push(resultData);
                }
                return acc;
              }, []),
            );
          }
          setHealthState(
            combinedFridgeHealth
              .flat()
              .map((value) => {
                if (value) {
                  return value;
                }
              })
              .filter((v) => {
                return v !== undefined;
              }),
          );
        })
        .catch((e) => {
          console.error(e);
          setIsLoading(false);
        });
    };
    const fetchlocations = async () => {
      let locations = await LocationIDs();
      locations = locations.data.locations.locations
        .filter((location) => location.type === 'FRIDGE')
        .sort((a, b) => (a.id > b.id ? 1 : -1));
      fetchHealth(locations);
    };
    fetchlocations();
  }, []);

  // Filter locations based on user input in forms
  function filterLocationList(location) {
    const val = Object.keys(fields)
      .filter((field) => fields[field] !== '')
      .reduce((accumulator, field) => {
        return (
          accumulator &&
          location[field] &&
          fields[field].toString() === location[field].toString()
        );
      }, true);
    return val;
  }

  return (
    <StyledDiv>
      <h1>Fridge Health Monitoring</h1>
      <BarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Bar dataKey="affected" stackId="a" fill="#DA3A44" />
        <Bar dataKey="total" stackId="a" fill="#2DB63B" />
      </BarChart>
      {isLoading && <Loader />}
      {!isLoading && (
        <section className="content">
          <div>
            <h3>Filters</h3>
            <div className="filters">
              {filterFields.map((field) => {
                if (field.type === 'bool') {
                  return (
                    <Toggle
                      field={{
                        name: `name-${field.key}`,
                        value: field.default,
                        onChange: handleFieldChange,
                        onBlur: () => {},
                      }}
                      id={field.key}
                      label={field.label}
                      key={`toggle${field.key}`}
                    />
                  );
                }
                if (field.type === 'select') {
                  return (
                    <Select
                      id={field.key}
                      field={field}
                      label={field.label}
                      onChange={handleFieldChange}
                      options={[
                        ...new Set(
                          healthState
                            .filter((loc) => loc[field.key])
                            .map((loc) => loc[field.key]),
                        ),
                      ]
                        .sort()
                        .map((i) => ({
                          label: i,
                          value: i,
                        }))}
                    />
                  );
                }
                return <></>;
              })}
            </div>
          </div>
          <div className="locationsWrapper">
            <h2 className="countWrapper">
              <span>Locations</span>
              <span className="count">
                {healthState.filter(filterLocationList).length}
              </span>
            </h2>
            {healthState.filter(filterLocationList).map((location) => (
              <Panel eventKey={location.locationId} key={location.locationId}>
                <Panel.Heading>
                  <Panel.Title toggle componentClass="h3">
                    {location.locationId}
                  </Panel.Title>
                </Panel.Heading>
                <Panel.Body collapsible>
                  <Table>
                    <thead>
                      <tr>
                        <th>Attribute</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(location).map((key) => (
                        <tr key={key}>
                          <td>{key}</td>
                          <td>
                            {location[key]
                              ? location[key].toString()
                              : 'No data'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Panel.Body>
              </Panel>
            ))}
          </div>
        </section>
      )}
    </StyledDiv>
  );
}
